'use strict';

/**
 * Pure rules for a customer's record-set manifests (ManifestSchema rows `LIMS-<RecordSet>-<Verb>`): how the List and
 * Filter verbs layer over the config app's own list columns and filter schema, and how the Document List / Filter
 * verbs map onto the Documents browser. No pict, no REST: CustomerRecordManifests-Provider.js feeds these.
 */

/**
 * @typedef {Object} ListColumn
 * @property {string} Key - the record field (or address) the column shows
 * @property {string} [DisplayName] - the header
 * @property {{ ValueTemplate?: string }} [PictDashboard] - how the cell renders
 * @property {boolean} [DefaultHidden]
 * @property {boolean} [Sortable] - set by the manifest; unset leaves it to the list
 */

/**
 * @typedef {Object} DocumentBrowseColumn
 * @property {string} Key - one of DOCUMENT_BROWSE_COLUMN_KEYS
 * @property {string} Label - the header
 */

/**
 * @typedef {Object} SelectOption
 * @property {string} id - the stored value
 * @property {string} text - the label
 */

/**
 * @typedef {Object} DocumentBrowseLayout
 * @property {Array<DocumentBrowseColumn>} Columns
 * @property {Array<SelectOption>|null} StatusOptions - null keeps the browser's own status list
 * @property {Array<SelectOption>|null} StateOptions - null means no State filter
 */

/**
 * The Documents browser's built-in columns, in its own order. `StateName` is the badge that shows the state name, or
 * the status when there is none.
 * @type {Array<DocumentBrowseColumn>}
 */
const DEFAULT_DOCUMENT_BROWSE_COLUMNS =
[
	{ Key: 'DocumentType', Label: 'Type' },
	{ Key: 'Name', Label: 'Document' },
	{ Key: 'DocumentDate', Label: 'Date' },
	{ Key: 'CreatingIDUser', Label: 'Author' },
	{ Key: 'StateName', Label: 'Status' },
];

/** The fields the Documents browser knows how to draw a cell for. */
const DOCUMENT_BROWSE_COLUMN_KEYS = [ 'DocumentType', 'Name', 'DocumentDate', 'CreatingIDUser', 'Status', 'StateName', 'IDProject' ];

/**
 * @param {any} pValue
 * @return {boolean}
 */
const isPlainObject = (pValue) => (pValue !== null) && (typeof pValue === 'object') && !Array.isArray(pValue);

/**
 * A manifest counts only when it has at least one descriptor. For List and Filter an empty row is treated as no row:
 * an empty list would show no columns and an empty filter set changes nothing. (For the form verbs an empty row turns
 * the verb off instead; that rule lives in SampleWorkspace-Provider.js loadFormAvailabilityAsync.)
 * @param {any} pManifest
 * @return {boolean}
 */
function hasDescriptors(pManifest)
{
	return isPlainObject(pManifest) && isPlainObject(pManifest.Descriptors) && (Object.keys(pManifest.Descriptors).length > 0);
}

/**
 * Turn a List manifest into list columns, the way pict-section-recordset's generateManifestTableCells does: one
 * column per descriptor that has a PictDashboard block, keyed by the descriptor's address, headed by its Name. A
 * column the manifest gives no ValueTemplate keeps the template the underlying column set has for the same Key, so
 * the manifest picks columns and headers while the platform keeps its cell renderers (e.g. the Status label).
 * @param {any} pManifest
 * @param {Array<ListColumn|string>} [pBaseColumns] - the columns beneath the manifest
 * @return {Array<ListColumn>} empty when the manifest has no usable descriptors
 */
function listManifestToColumns(pManifest, pBaseColumns)
{
	if (!hasDescriptors(pManifest))
	{
		return [];
	}
	const tmpBaseTemplates = {};
	for (const tmpBase of (Array.isArray(pBaseColumns) ? pBaseColumns : []))
	{
		if (isPlainObject(tmpBase) && tmpBase.Key && tmpBase.PictDashboard && tmpBase.PictDashboard.ValueTemplate)
		{
			tmpBaseTemplates[tmpBase.Key] = tmpBase.PictDashboard.ValueTemplate;
		}
	}
	const tmpColumns = [];
	for (const [ tmpKey, tmpDescriptor ] of Object.entries(pManifest.Descriptors))
	{
		if (!isPlainObject(tmpDescriptor) || !isPlainObject(tmpDescriptor.PictDashboard))
		{
			continue;
		}
		const tmpColumn = { Key: tmpKey, DisplayName: tmpDescriptor.Name || tmpKey };
		const tmpTemplate = tmpDescriptor.PictDashboard.ValueTemplate || tmpBaseTemplates[tmpKey];
		if (tmpTemplate)
		{
			tmpColumn.PictDashboard = Object.assign({}, tmpDescriptor.PictDashboard, { ValueTemplate: tmpTemplate });
		}
		if (tmpDescriptor.DefaultHidden === true)
		{
			tmpColumn.DefaultHidden = true;
		}
		if (typeof tmpDescriptor.Sortable === 'boolean')
		{
			tmpColumn.Sortable = tmpDescriptor.Sortable;
		}
		tmpColumns.push(tmpColumn);
	}
	return tmpColumns;
}

/**
 * Whether the cascaded configuration sets list columns at a level above Global (Customer, Project or User). The
 * cascade arrives merged, so this compares it with the Global level on its own: equal (or no columns at all) means
 * nothing above Global touched them.
 * @param {Array<any>|undefined} pCascadedColumns - Columns from the merged cascade, if any
 * @param {Array<any>|undefined} pGlobalColumns - Columns at the Global level alone, if any
 * @return {boolean}
 */
function hasColumnsAboveGlobal(pCascadedColumns, pGlobalColumns)
{
	if (!Array.isArray(pCascadedColumns))
	{
		return false;
	}
	if (!Array.isArray(pGlobalColumns))
	{
		return true;
	}
	return JSON.stringify(pCascadedColumns) !== JSON.stringify(pGlobalColumns);
}

/**
 * Decide a list's columns. Precedence, lowest first: code seed, Global configuration, the customer's List manifest,
 * then Customer / Project / User configuration.
 * @param {Object} pInput
 * @param {any} pInput.Manifest - the customer's List manifest (or null)
 * @param {Array<any>} [pInput.SeedColumns] - the code seed's columns
 * @param {Array<any>} [pInput.GlobalColumns] - the Global configuration's columns, if any
 * @param {Array<any>} [pInput.CascadedColumns] - the merged cascade's columns, if any
 * @return {{ Columns: Array<any>|undefined, Source: string }} Source is 'Configuration', 'Manifest', 'Global' or 'Seed'
 */
function resolveListColumns(pInput)
{
	const tmpInput = pInput || {};
	if (hasColumnsAboveGlobal(tmpInput.CascadedColumns, tmpInput.GlobalColumns))
	{
		return { Columns: tmpInput.CascadedColumns, Source: 'Configuration' };
	}
	const tmpBase = Array.isArray(tmpInput.GlobalColumns) ? tmpInput.GlobalColumns : tmpInput.SeedColumns;
	const tmpManifestColumns = listManifestToColumns(tmpInput.Manifest, tmpBase);
	if (tmpManifestColumns.length > 0)
	{
		return { Columns: tmpManifestColumns, Source: 'Manifest' };
	}
	if (Array.isArray(tmpInput.GlobalColumns))
	{
		return { Columns: tmpInput.GlobalColumns, Source: 'Global' };
	}
	return { Columns: tmpInput.SeedColumns, Source: 'Seed' };
}

/**
 * A customer filter that replaces the platform's still searches every column the platform's searched: a join clause
 * keeps its own columns first, then gains the platform clause's (same RemoteTable) that it lacks. LADOTD's Material
 * filter searches Name only; the platform's also searches the material codes.
 * @param {any} pBaseDescriptor - the platform's filter for this key
 * @param {any} pOverlayDescriptor - the customer's filter for the same key (modified in place)
 * @return {any} the overlay descriptor
 */
function keepBaseSearchColumns(pBaseDescriptor, pOverlayDescriptor)
{
	const tmpBaseClauses = (pBaseDescriptor && Array.isArray(pBaseDescriptor.AvailableClauses)) ? pBaseDescriptor.AvailableClauses : [];
	const tmpOverlayClauses = (pOverlayDescriptor && Array.isArray(pOverlayDescriptor.AvailableClauses)) ? pOverlayDescriptor.AvailableClauses : [];
	tmpOverlayClauses.forEach((pClause) =>
	{
		if (!pClause || !Array.isArray(pClause.ExternalFilterByColumns))
		{
			return;
		}
		const tmpBaseClause = tmpBaseClauses.find((pBase) => pBase && Array.isArray(pBase.ExternalFilterByColumns) && pBase.RemoteTable && (pBase.RemoteTable === pClause.RemoteTable));
		if (!tmpBaseClause)
		{
			return;
		}
		tmpBaseClause.ExternalFilterByColumns.forEach((pColumn) =>
		{
			if (!pClause.ExternalFilterByColumns.includes(pColumn))
			{
				pClause.ExternalFilterByColumns.push(pColumn);
			}
		});
	});
	return pOverlayDescriptor;
}

/**
 * Layer a Filter manifest over a record set's filter schema, as legacy LIMS did: each descriptor replaces the filter of
 * the same key (or adds one), and `ReplaceBaseFilters: true` drops every filter the manifest doesn't name. A replaced
 * filter keeps the platform's search columns (keepBaseSearchColumns).
 * @param {Record<string, any>} pBaseSchema - the record set's own filter schema (not modified)
 * @param {any} pManifest - the customer's Filter manifest (or null)
 * @return {Record<string, any>} a new schema; a copy of the base when the manifest has no descriptors
 */
function mergeFilterSchema(pBaseSchema, pManifest)
{
	const tmpBase = isPlainObject(pBaseSchema) ? pBaseSchema : {};
	if (!hasDescriptors(pManifest))
	{
		return Object.assign({}, tmpBase);
	}
	const tmpOverlay = JSON.parse(JSON.stringify(pManifest.Descriptors));
	Object.keys(tmpOverlay).forEach((pKey) =>
	{
		if (tmpBase[pKey])
		{
			keepBaseSearchColumns(tmpBase[pKey], tmpOverlay[pKey]);
		}
	});
	if (pManifest.ReplaceBaseFilters === true)
	{
		return tmpOverlay;
	}
	return Object.assign({}, tmpBase, tmpOverlay);
}

/**
 * The pick-list options a Filter manifest declares for one field: the first clause with `PictForm.SelectOptions`.
 * @param {any} pManifest
 * @param {string} pKey - descriptor key
 * @return {Array<SelectOption>|null}
 */
function filterSelectOptions(pManifest, pKey)
{
	if (!hasDescriptors(pManifest) || !isPlainObject(pManifest.Descriptors[pKey]))
	{
		return null;
	}
	const tmpClauses = Array.isArray(pManifest.Descriptors[pKey].AvailableClauses) ? pManifest.Descriptors[pKey].AvailableClauses : [];
	for (const tmpClause of tmpClauses)
	{
		const tmpOptions = tmpClause && tmpClause.PictForm && tmpClause.PictForm.SelectOptions;
		if (Array.isArray(tmpOptions) && (tmpOptions.length > 0))
		{
			return tmpOptions
				.filter((pOption) => pOption && (pOption.id !== undefined) && (pOption.id !== null) && (String(pOption.id) !== ''))
				.map((pOption) => ({ id: String(pOption.id), text: String((pOption.text === undefined) || (pOption.text === null) ? pOption.id : pOption.text) }));
		}
	}
	return null;
}

/**
 * Map a customer's Document List and Filter manifests onto the Documents browser. The browser draws its own cells, so
 * a List manifest chooses which of the known columns show, in what order, under what header; descriptors it can't
 * draw (and value templates) are ignored. A Filter manifest supplies the Status pick list and adds a State pick list;
 * the browser's Type and Project filters already cover those descriptors.
 * @param {any} pListManifest
 * @param {any} pFilterManifest
 * @return {DocumentBrowseLayout}
 */
function documentBrowseLayout(pListManifest, pFilterManifest)
{
	let tmpColumns = DEFAULT_DOCUMENT_BROWSE_COLUMNS.map((pColumn) => Object.assign({}, pColumn));
	if (hasDescriptors(pListManifest))
	{
		const tmpFromManifest = [];
		for (const [ tmpKey, tmpDescriptor ] of Object.entries(pListManifest.Descriptors))
		{
			if ((DOCUMENT_BROWSE_COLUMN_KEYS.indexOf(tmpKey) < 0) || tmpFromManifest.some((pColumn) => pColumn.Key === tmpKey))
			{
				continue;
			}
			tmpFromManifest.push({ Key: tmpKey, Label: (isPlainObject(tmpDescriptor) && tmpDescriptor.Name) ? String(tmpDescriptor.Name) : tmpKey });
		}
		if (tmpFromManifest.length > 0)
		{
			tmpColumns = tmpFromManifest;
		}
	}
	return {
		Columns: tmpColumns,
		StatusOptions: filterSelectOptions(pFilterManifest, 'Status'),
		StateOptions: filterSelectOptions(pFilterManifest, 'StateName'),
	};
}

module.exports =
{
	DEFAULT_DOCUMENT_BROWSE_COLUMNS,
	DOCUMENT_BROWSE_COLUMN_KEYS,
	hasDescriptors,
	listManifestToColumns,
	hasColumnsAboveGlobal,
	resolveListColumns,
	mergeFilterSchema,
	filterSelectOptions,
	documentBrowseLayout,
};
